/**
 * Lớp vận chuyển giả, đặt **ở tầng repository** (§14.5).
 *
 * Nhờ đặt ở đây mà `queryKey`, `staleTime` và việc vô hiệu hóa cache hoạt động
 * đúng như lúc chạy thật; trạng thái tải là thật, không phải giả bằng
 * `setTimeout` trong view. Khi có API thật thì mỗi domain chỉ sửa một tệp.
 */
import type { ApiError } from '../lib/errors';

/** Trạng thái cần diễn, chọn bằng tham số `?mock=` trên URL (§14.2). */
export type MockState = 'data' | 'loading' | 'empty' | 'error' | 'filtered-empty';

export function mockState(): MockState {
  if (typeof window === 'undefined') return 'data';
  const value = new URLSearchParams(window.location.search).get('mock');
  switch (value) {
    case 'loading':
    case 'empty':
    case 'error':
    case 'filtered-empty':
      return value;
    default:
      return 'data';
  }
}

/** Độ trễ giả: đủ lâu để thực sự thấy được trạng thái tải và nút bị khóa. */
const DELAY_MS = 450;

const NETWORK_ERROR: ApiError = {
  code: 'NETWORK_ERROR',
  message: 'Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.',
};

/**
 * Trả về dữ liệu giả sau một nhịp chờ, hoặc diễn trạng thái mà `?mock=` yêu cầu.
 *
 * `loading` không bao giờ resolve, nên màn hình đứng ở trạng thái tải để xem
 * skeleton; `error` ném `ApiError` đã chuẩn hóa để view chạy nhánh lỗi.
 */
export async function mockRequest<T>(data: T, emptyValue?: T): Promise<T> {
  const state = mockState();

  if (state === 'loading') {
    return new Promise<T>(() => {
      // Cố ý không bao giờ hoàn tất.
    });
  }

  await new Promise((resolve) => setTimeout(resolve, DELAY_MS));

  if (state === 'error') throw NETWORK_ERROR;
  if (state === 'empty' && emptyValue !== undefined) return emptyValue;

  return data;
}
